// AIConsentModal.jsx
//
// Mounted once at the AppShell level. Subscribes to the consent module's
// modal pub/sub. When a feature calls requestAIConsent() and consent
// hasn't been granted yet, this opens a sheet that:
//   - explains what data is being sent
//   - identifies the third-party recipients (Anthropic, ElevenLabs,
//     OpenAI Whisper) and what each one is used for
//   - has explicit Continue / Cancel buttons
//
// Required by Apple App Review guideline 5.1.1(i) / 5.1.2(i): in-app
// disclosure + consent before sharing personal data with a third-party
// AI service. Privacy policy alone is not sufficient per Apple's
// wording.

import { useEffect, useState } from 'react';
import {
  isModalOpen,
  resolveAIConsent,
  subscribeModal,
} from '../lib/aiConsent.js';

export function AIConsentModal() {
  const [open, setOpen] = useState(isModalOpen());

  useEffect(() => {
    const unsub = subscribeModal(() => setOpen(isModalOpen()));
    return unsub;
  }, []);

  if (!open) return null;

  const onContinue = () => resolveAIConsent(true);
  const onCancel = () => resolveAIConsent(false);

  return (
    <>
      <div
        onClick={onCancel}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
          zIndex: 220, animation: 'fadeIn 140ms ease-out',
        }}
      />
      {/* Flex-centered wrapper. Splitting centering from animation
          avoids the sheetIn keyframe overwriting the centering
          transform mid-animation. */}
      <div
        style={{
          position: 'fixed', inset: 0, zIndex: 221,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 24,
          pointerEvents: 'none',
        }}
      >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="ai-consent-title"
        style={{
          width: '100%',
          maxWidth: 460,
          maxHeight: '80vh',
          overflowY: 'auto',
          background: 'rgba(20,24,32,0.98)',
          border: '1px solid var(--stroke-1)',
          borderRadius: 20,
          padding: '22px 22px 18px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.7)',
          animation: 'sheetIn 220ms cubic-bezier(0.22,1,0.36,1)',
          pointerEvents: 'auto',
        }}
      >
        <div
          id="ai-consent-title"
          style={{
            fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 17,
            color: 'var(--fg-1)', marginBottom: 10,
          }}
        >
          Use AI features?
        </div>

        <div
          style={{
            fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)',
            lineHeight: 1.55, marginBottom: 14,
          }}
        >
          Consistency uses third-party AI services for the Agenda, Intel
          Brief, and narration features. To use these, the app sends the
          following data outside our servers:
        </div>

        <div
          style={{
            fontFamily: 'var(--font-body)', fontSize: 12.5, color: 'var(--fg-2)',
            lineHeight: 1.55, marginBottom: 14,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid var(--stroke-1)',
            borderRadius: 12,
            padding: '12px 14px',
          }}
        >
          <div style={{ marginBottom: 8 }}>
            <strong style={{ color: 'var(--fg-1)' }}>Anthropic (Claude)</strong>
            <span style={{ color: 'var(--fg-3)' }}>
              {' '}— receives the text you type when you build an Agenda or generate an Intel Brief, so the model can structure the response.
            </span>
          </div>
          <div style={{ marginBottom: 8 }}>
            <strong style={{ color: 'var(--fg-1)' }}>ElevenLabs</strong>
            <span style={{ color: 'var(--fg-3)' }}>
              {' '}— receives the narration text of an Intel Brief when you tap Play, so it can return audio.
            </span>
          </div>
          <div>
            <strong style={{ color: 'var(--fg-1)' }}>OpenAI (Whisper)</strong>
            <span style={{ color: 'var(--fg-3)' }}>
              {' '}— receives the audio you record when you use voice input on the Agenda, so it can transcribe it.
            </span>
          </div>
        </div>

        <div
          style={{
            fontFamily: 'var(--font-body)', fontSize: 12.5, color: 'var(--fg-3)',
            lineHeight: 1.55, marginBottom: 18,
          }}
        >
          Under our API terms, none of these providers train their models
          on data you send through Consistency. You can revoke this
          permission at any time under Profile → Privacy.
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 999,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid var(--stroke-1)',
              color: 'var(--fg-2)',
              fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13.5,
              cursor: 'pointer',
            }}
          >
            Not now
          </button>
          <button
            onClick={onContinue}
            style={{
              flex: 1, padding: '12px 16px', borderRadius: 999,
              background: 'linear-gradient(180deg, #47A6FF, #2D7DE0)',
              border: '1px solid rgba(71,166,255,0.55)',
              color: '#ffffff',
              fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13.5,
              cursor: 'pointer',
              boxShadow: '0 8px 22px rgba(45,125,224,0.4)',
            }}
          >
            Continue
          </button>
        </div>
      </div>
      </div>
    </>
  );
}
